import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import useDashboardCompact from '../../../styles/theme/surfaces/useDashboardCompact';

/**
 * Standard dashboard page header: icon + title, breadcrumb trail below,
 * primary/secondary actions aligned to the right. Meant to sit in
 * `PageLayout`'s `header` slot.
 *
 * `breadcrumbs`: [{ label, to? }]. The last entry is always rendered as the
 * current page (text.primary, no link). Earlier entries render as a router
 * `Link` when `to` is given, otherwise as plain muted text.
 */
export default function PageHeader({ icon, title, breadcrumbs = [], actions }) {
  const compact = useDashboardCompact();

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
      <Stack spacing={compact ? 0.5 : 1}>
        <Stack direction="row" alignItems="center" spacing={1}>
          {icon}
          <Typography variant="h5">
            {title}
          </Typography>
        </Stack>
        {breadcrumbs.length > 0 && (
          <Breadcrumbs aria-label="breadcrumb">
            {breadcrumbs.map((crumb, index) => {
              const isLast = index === breadcrumbs.length - 1;
              if (isLast || !crumb.to) {
                return (
                  <Typography key={crumb.label} color={isLast ? 'text.primary' : 'text.secondary'}>
                    {crumb.label}
                  </Typography>
                );
              }
              return (
                <Link key={crumb.label} component={RouterLink} to={crumb.to} underline="hover" color="text.secondary">
                  {crumb.label}
                </Link>
              );
            })}
          </Breadcrumbs>
        )}
      </Stack>
      {actions && (
        <Stack direction="row" spacing={1}>
          {actions}
        </Stack>
      )}
    </Stack>
  );
}
